import { useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import FilterBar from "@/components/admin/FilterBar";
import { exportCsv } from "@/lib/csv";
import { toast } from "sonner";
import {
    EMPTY_BASIC_FILTERS,
    matchesText,
    withinDateRange,
} from "@/components/admin/adminFilters";

export default function VendorsTab({ vendors }) {
    const [filters, setFilters] = useState({ ...EMPTY_BASIC_FILTERS, service_type: "" });

    const filtered = useMemo(() => {
        const f = filters;
        return vendors.filter((v) => {
            if (
                !matchesText(
                    [v.full_name, v.business_name, v.email, v.phone, v.service_area, v.message],
                    f.q
                )
            )
                return false;
            if (
                f.service_type &&
                (v.service_type || "").toLowerCase().indexOf(f.service_type.toLowerCase()) === -1
            )
                return false;
            const created = (v.created_at || "").slice(0, 10);
            if ((f.date_from || f.date_to) && !withinDateRange(created, f.date_from, f.date_to))
                return false;
            return true;
        });
    }, [vendors, filters]);

    function exportRows() {
        const headers = [
            { key: "full_name", label: "Name" },
            { key: "business_name", label: "Business" },
            { key: "service_type", label: "Service type" },
            { key: "service_area", label: "Service area" },
            { key: "email", label: "Email" },
            { key: "phone", label: "Phone" },
            { key: "years_experience", label: "Years experience" },
            { key: "rates", label: "Rates" },
            { key: "insurance_status", label: "Insurance" },
            { key: "message", label: "Message" },
            { key: "created_at", label: "Created at" },
        ];
        exportCsv(`vendors_${new Date().toISOString().slice(0, 10)}.csv`, headers, filtered);
        toast.success(`Exported ${filtered.length} vendor(s)`);
    }

    return (
        <>
            <FilterBar
                fields={[
                    { key: "q", label: "Search", type: "text" },
                    { key: "service_type", label: "Service type contains", type: "text" },
                    { key: "date_from", label: "Applied from", type: "date" },
                    { key: "date_to", label: "Applied to", type: "date" },
                ]}
                value={filters}
                onChange={setFilters}
                onExport={exportRows}
                resultCount={filtered.length}
                totalCount={vendors.length}
            />
            <div className="card-soft mt-4 overflow-x-auto p-4">
                <Table data-testid="vendors-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name / Business</TableHead>
                            <TableHead>Service</TableHead>
                            <TableHead>Contact</TableHead>
                            <TableHead>Area</TableHead>
                            <TableHead>Created</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.map((v) => (
                            <TableRow key={v.id}>
                                <TableCell>
                                    <p className="font-medium">{v.full_name}</p>
                                    <p className="text-xs text-[#475569]">{v.business_name}</p>
                                </TableCell>
                                <TableCell>{v.service_type}</TableCell>
                                <TableCell>
                                    <p className="text-xs">{v.email}</p>
                                    <p className="text-xs">{v.phone}</p>
                                </TableCell>
                                <TableCell>{v.service_area}</TableCell>
                                <TableCell className="text-xs text-[#475569]">
                                    {v.created_at?.slice(0, 16)}
                                </TableCell>
                            </TableRow>
                        ))}
                        {filtered.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} className="py-8 text-center text-sm text-[#475569]">
                                    No vendor applications match these filters.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </>
    );
}
